import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { AlertCircle, CheckCircle } from 'lucide-react';
import {
  validatePaymentSplit,
  calculateKokoChargeForSplit,
  calculateMintPayChargeForSplit,
  getPaymentMethodsUsed,
} from '../firebase/paymentSplitService';

const PaymentSplitDialog = ({
  open,
  onClose,
  onConfirm,
  totalAmount,
  billingSettings = {},
  currentPaymentType = 'Cash',
}) => {
  const [split, setSplit] = useState({
    cash: 0,
    bank: 0,
    koko: 0,
    mintpay: 0,
  });

  const [enabledMethods, setEnabledMethods] = useState({
    cash: false,
    bank: false,
    koko: false,
    mintpay: false,
  });

  const [validation, setValidation] = useState({ valid: false, error: '' });

  // Initialize split based on current payment type
  useEffect(() => {
    if (open) {
      const newSplit = { cash: 0, bank: 0, koko: 0, mintpay: 0 };
      const newEnabled = { cash: false, bank: false, koko: false, mintpay: false };

      // Set initial method based on current payment type
      if (currentPaymentType === 'Cash') {
        newSplit.cash = totalAmount;
        newEnabled.cash = true;
      } else if (currentPaymentType === 'Card' || currentPaymentType === 'Bank') {
        newSplit.bank = totalAmount;
        newEnabled.bank = true;
      } else if (currentPaymentType === 'Koko') {
        newSplit.koko = totalAmount;
        newEnabled.koko = true;
      } else if (currentPaymentType === 'MintPay') {
        newSplit.mintpay = totalAmount;
        newEnabled.mintpay = true;
      }

      setSplit(newSplit);
      setEnabledMethods(newEnabled);
    }
  }, [open, totalAmount, currentPaymentType]);

  // Validate whenever split changes
  useEffect(() => {
    const result = validatePaymentSplit(split, totalAmount, billingSettings);
    setValidation(result);
  }, [split, totalAmount, billingSettings]);

  const handleMethodToggle = (method) => {
    const newEnabled = { ...enabledMethods, [method]: !enabledMethods[method] };
    setEnabledMethods(newEnabled);

    // Reset split for disabled methods
    const newSplit = { ...split };
    if (!newEnabled[method]) {
      newSplit[method] = 0;
    }
    setSplit(newSplit);
  };

  const handleAmountChange = (method, value) => {
    const amount = parseFloat(value) || 0;
    setSplit(prev => ({
      ...prev,
      [method]: Math.max(0, amount),
    }));
  };

  const handleDistributeEvenly = () => {
    const enabledCount = Object.values(enabledMethods).filter(v => v).length;
    if (enabledCount === 0) return;

    const amountPerMethod = totalAmount / enabledCount;
    const newSplit = { cash: 0, bank: 0, card: 0, koko: 0, mintpay: 0 };

    Object.keys(enabledMethods).forEach(method => {
      if (enabledMethods[method]) {
        newSplit[method] = amountPerMethod;
      }
    });

    setSplit(newSplit);
  };

  const handleConfirm = () => {
    if (validation.valid) {
      onConfirm(split);
      onClose();
    }
  };

  const kokoCharge = calculateKokoChargeForSplit(split.koko, billingSettings?.kokoChargePercentage || 12);
  const mintPayCharge = calculateMintPayChargeForSplit(split.mintpay, billingSettings?.mintPayChargePercentage || 12);
  const totalWithCharges = Object.values(split).reduce((a, b) => a + b, 0) + kokoCharge + mintPayCharge;
  const methods = getPaymentMethodsUsed(split);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <span>Split Payment</span>
          <Chip label={`Total: ${totalAmount.toFixed(2)}`} size="small" color="primary" />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Payment Methods Selection */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Select Payment Methods
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Cash */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabledMethods.cash}
                        onChange={() => handleMethodToggle('cash')}
                      />
                    }
                    label="Cash"
                    sx={{ flex: 0 }}
                  />
                  {enabledMethods.cash && (
                    <TextField
                      type="number"
                      size="small"
                      value={split.cash || ''}
                      onChange={e => handleAmountChange('cash', e.target.value)}
                      inputProps={{ step: '0.01', min: '0' }}
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>

                {/* Bank/Card */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabledMethods.bank}
                        onChange={() => handleMethodToggle('bank')}
                      />
                    }
                    label="Bank/Card"
                    sx={{ flex: 0 }}
                  />
                  {enabledMethods.bank && (
                    <TextField
                      type="number"
                      size="small"
                      value={split.bank || ''}
                      onChange={e => handleAmountChange('bank', e.target.value)}
                      inputProps={{ step: '0.01', min: '0' }}
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>

                {/* Koko */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabledMethods.koko}
                        onChange={() => handleMethodToggle('koko')}
                      />
                    }
                    label="Koko"
                    sx={{ flex: 0 }}
                  />
                  {enabledMethods.koko && (
                    <TextField
                      type="number"
                      size="small"
                      value={split.koko || ''}
                      onChange={e => handleAmountChange('koko', e.target.value)}
                      inputProps={{ step: '0.01', min: '0' }}
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>

                {/* MintPay */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enabledMethods.mintpay}
                        onChange={() => handleMethodToggle('mintpay')}
                      />
                    }
                    label="MintPay"
                    sx={{ flex: 0 }}
                  />
                  {enabledMethods.mintpay && (
                    <TextField
                      type="number"
                      size="small"
                      value={split.mintpay || ''}
                      onChange={e => handleAmountChange('mintpay', e.target.value)}
                      inputProps={{ step: '0.01', min: '0' }}
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Distribute Evenly Button */}
              {Object.values(enabledMethods).filter(v => v).length > 1 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDistributeEvenly}
                  sx={{ mt: 2 }}
                >
                  Distribute Evenly
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Validation Alert */}
          {!validation.valid && (
            <Alert severity="error" icon={<AlertCircle size={20} />}>
              {validation.error}
            </Alert>
          )}

          {validation.valid && (
            <Alert severity="success" icon={<CheckCircle size={20} />}>
              Payment split is valid ✓
            </Alert>
          )}

          {/* Koko Charge Notice */}
          {split.koko > 0 && kokoCharge > 0 && (
            <Alert severity="info">
              Koko charge: {(billingSettings?.kokoChargePercentage || 12)}% = {kokoCharge.toFixed(2)}
            </Alert>
          )}

          {/* MintPay Charge Notice */}
          {split.mintpay > 0 && mintPayCharge > 0 && (
            <Alert severity="info">
              MintPay charge: {(billingSettings?.mintPayChargePercentage || 12)}% = {mintPayCharge.toFixed(2)}
            </Alert>
          )}

          {/* Summary */}
          {methods.length > 0 && (
            <Card variant="outlined" sx={{ 
              bgcolor: 'background.paper',
              color: 'text.primary'
            }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Payment Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {methods.map(m => (
                    <Box key={m.method} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{m.label}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {m.amount.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  {kokoCharge > 0 && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Koko Charge:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#ff9800' }}>
                          +{kokoCharge.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {mintPayCharge > 0 && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">MintPay Charge:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#ff9800' }}>
                          +{mintPayCharge.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Total:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2196f3' }}>
                      {totalWithCharges.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!validation.valid}
        >
          Confirm Split
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentSplitDialog;
